import Spinner from '~/components/Spinner/Spinner';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { unstable_batchedUpdates } from 'react-dom';
import { fetchBuildingDetailsSuccess } from '~/reducers/building';
import BuildingCard from '~/components/BuildingCard/BuildingCard';
import { useHistory } from 'react-router-dom';
import { Building } from '~/types/building';
import { fetchBuildingsPageRequest } from '~/api/building';
import ServerPagination from '~/components/ServerPagination/ServerPagination';
import './BuildingsListContainer.css';

const DEBOUNCE_MS = 300;

export const BuildingList: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => {
    const timer = setTimeout(() => {
      // React 16 only batches inside event handlers; one render = one request.
      unstable_batchedUpdates(() => {
        setQ(search.trim());
        setPage(1);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery(
    ['user-buildings-page', page, pageSize, q],
    () =>
      fetchBuildingsPageRequest({ page, pageSize, q }).then((res) => res.data),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const onClick = (uuid: string, building: Building) => {
    dispatch(fetchBuildingDetailsSuccess(building));
    history.push(`/building/${uuid}`);
  };

  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Spinner />
      </div>
    );
  }

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <p className='buildinglist-title'>Choose your building</p>
      <input
        type='text'
        placeholder='Search properties…'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          maxWidth: '360px',
          padding: '8px 12px',
          marginBottom: '8px',
          fontSize: '14px',
          border: '1px solid #d0d0d0',
          borderRadius: '6px',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
      <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px 0' }}>
        {total} {total === 1 ? 'property' : 'properties'}
      </p>
      <div className='buildinglist-grid'>
        {data?.buildings.map((b: Building) => (
          <BuildingCard buildingCard={b} onClick={onClick} key={b.uuid} />
        ))}
        {q && total === 0 && (
          <p style={{ color: '#888', fontSize: '14px' }}>
            No properties match your search.
          </p>
        )}
      </div>
      <ServerPagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </>
  );
};
